import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StarSpinner } from "@/components/StarSpinner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminStationery — a consolidated stationery drawer for the firm.
 * Every print-ready asset in one editorial index; downloads are token-based
 * for standing pieces and form-driven for per-client pieces.
 */
export default function AdminStationery({ onGoTo }) {
  const [certEmail, setCertEmail] = useState("");
  const [certLoading, setCertLoading] = useState(false);
  const [linkedInPost, setLinkedInPost] = useState(null);
  const [envelopeOpen, setEnvelopeOpen] = useState(false);
  const [envRecipient, setEnvRecipient] = useState("");
  const [envLine1, setEnvLine1] = useState("");
  const [envLine2, setEnvLine2] = useState("");
  const [envLoading, setEnvLoading] = useState(false);

  const openLinkedInAdaptation = async () => {
    try {
      const { data } = await api.get("/admin/state-of-firm/linkedin");
      setLinkedInPost(data);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not load LinkedIn adaptation");
    }
  };

  const openEnvelopeForm = () => {
    setEnvRecipient("");
    setEnvLine1("");
    setEnvLine2("");
    setEnvelopeOpen(true);
  };

  const submitEnvelope = async (e) => {
    e.preventDefault();
    setEnvLoading(true);
    try {
      const { data } = await api.post(
        "/admin/envelope.pdf",
        {
          recipient_name: envRecipient || null,
          recipient_line1: envLine1 || null,
          recipient_line2: envLine2 || null,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "LSC-Envelope-No10.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Envelope PDF ready");
      setEnvelopeOpen(false);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Envelope render failed");
    } finally {
      setEnvLoading(false);
    }
  };

  const downloadCert = async () => {
    if (!certEmail) {
      toast.error("Enter a client email");
      return;
    }
    setCertLoading(true);
    try {
      const encoded = encodeURIComponent(certEmail.toLowerCase().trim());
      const { data } = await api.get(
        `/admin/ip-assets/${encoded}/certificate.pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = certEmail.toLowerCase().replace("@", "-at-").replace(/\./g, "-");
      a.download = `LSC-IP-Stewardship-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Certificate download failed");
    } finally {
      setCertLoading(false);
    }
  };

  return (
    <div className="mt-10" data-testid="admin-stationery-tab">
      <div className="hairline-bottom pb-6 mb-10">
        <div className="kicker">Firm Stationery</div>
        <h2 className="mt-3 font-serif text-3xl text-navy">The stationery drawer</h2>
        <p className="mt-3 max-w-2xl text-slate2 font-light">
          Every print-ready document the firm keeps on hand — welcome kit, letters, and
          certificates. Grab any piece from one place.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brass/30" data-testid="stationery-grid">

        {/* Welcome Kit */}
        <Card kicker="Season One" title="Welcome Kit" body="A folded 4-panel print piece introducing the firm — front cover, contact panel, founder's note, and the Four Promises. Handed to a new client at the first meeting." testId="stationery-welcome-kit">
          <a
            href={`${BACKEND}/api/admin/welcome-kit.pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-brass inline-flex items-center gap-2"
            data-testid="stationery-welcome-download"
          >
            <span className="text-lg leading-none">&#9733;</span>
            Download Print PDF
          </a>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            11 × 8.5 landscape · Duplex · Fold in half
          </div>
        </Card>

        {/* Letter of Engagement */}
        <Card kicker="Composed Per Client" title="Letter of Engagement" body="Formal welcome-and-scope letter with the bronze wax seal, signature block, and countersignature line. Rendered from a form so each letter is tailored." testId="stationery-engagement">
          <button
            onClick={() => onGoTo?.("engagement")}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="stationery-engagement-compose"
          >
            <span className="text-brass text-lg leading-none">&#9733;</span>
            Compose New Letter
          </button>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            Letter portrait · Bronze wax seal · 1–2 pages
          </div>
        </Card>

        {/* IP Stewardship Certificate */}
        <Card kicker="Per Client · Live Records" title="Certificate of IP Stewardship" body="Live-generated portfolio summary of every trademark, copyright, domain, and other IP asset the firm holds for a specific client. Includes the disclaimer that it is not an official government certificate." testId="stationery-ip-cert">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              placeholder="client@example.com"
              value={certEmail}
              onChange={(e) => setCertEmail(e.target.value)}
              className="field !mb-0 flex-1"
              data-testid="stationery-cert-email"
            />
            <button
              onClick={downloadCert}
              disabled={certLoading}
              className="btn-brass inline-flex items-center gap-2 shrink-0 disabled:opacity-70"
              data-testid="stationery-cert-download"
            >
              {certLoading ? (
                <>
                  <StarSpinner size="sm" label="" />
                  <span>Rendering…</span>
                </>
              ) : (
                <>
                  <span className="text-lg leading-none">&#9733;</span>
                  <span>Generate</span>
                </>
              )}
            </button>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            Letter portrait · Auto-populated from live IP records
          </div>
        </Card>

        {/* State of the Firm — the standing quarterly letter */}
        <Card kicker="Quarterly · Standing" title="State of the Firm" body="The quarterly print letter to clients — a signed 5-page dispatch on firm developments, industry notes, and the year&apos;s counsel priorities. Dynamically rendered on demand." testId="stationery-state-of-firm">
          <div className="flex flex-wrap gap-2">
            <a
              href={`${BACKEND}/api/admin/state-of-firm.pdf`}
              target="_blank"
              rel="noreferrer"
              className="btn-brass inline-flex items-center gap-2"
              data-testid="stationery-sof-download"
            >
              <span className="text-lg leading-none">&#9733;</span>
              Print PDF (5-page)
            </a>
            <button
              type="button"
              onClick={openLinkedInAdaptation}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="stationery-sof-linkedin"
            >
              <span className="text-brass text-lg leading-none">&#9733;</span>
              LinkedIn Adaptation
            </button>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            Letter portrait · Dynamically rendered · Copy-ready social version
          </div>
        </Card>

        {/* Envelope — matching No. 10 */}
        <Card kicker="Matching · No. 10" title="Business Envelope" body="A matching No. 10 business envelope (9.5&Prime; × 4.125&Prime;) with the return address, bronze star, and firm mark. Composed per recipient or blank for a stack." testId="stationery-envelope">
          <button
            type="button"
            onClick={openEnvelopeForm}
            className="btn-brass inline-flex items-center gap-2"
            data-testid="stationery-envelope-open"
          >
            <span className="text-lg leading-none">&#9733;</span>
            Compose Envelope
          </button>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            9.5&Prime; × 4.125&Prime; · Return address preset
          </div>
        </Card>

        {/* Holiday Card — annual */}
        <Card kicker="Annual · December" title="Holiday Card" body="The firm&apos;s year-end card. Folded 4-panel print piece with a bronze star wreath, a signed year-end note, and matching contact block. Refreshes each December." testId="stationery-holiday-card">
          <a
            href={`${BACKEND}/api/admin/holiday-card.pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-brass inline-flex items-center gap-2"
            data-testid="stationery-holiday-download"
          >
            <span className="text-lg leading-none">&#9733;</span>
            Download Print PDF
          </a>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
            11 × 8.5 landscape · Duplex · Fold in half
          </div>
        </Card>

      </div>

      {/* LinkedIn preview modal */}
      {linkedInPost && (
        <div
          className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="linkedin-preview-modal"
          onClick={() => setLinkedInPost(null)}
        >
          <div
            className="bg-ivory max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="kicker">LinkedIn Adaptation</div>
                <h3 className="mt-2 font-serif text-2xl text-navy">{linkedInPost.title}</h3>
              </div>
              <button
                onClick={() => setLinkedInPost(null)}
                className="text-slate2 hover:text-navy text-xl leading-none"
                aria-label="Close"
                data-testid="linkedin-modal-close"
              >
                ×
              </button>
            </div>
            <p className="text-sm font-cormorant italic text-slate2 mb-4">
              {linkedInPost.subtitle}
            </p>
            <textarea
              readOnly
              value={linkedInPost.post_text}
              rows={18}
              className="w-full font-mono text-[11.5px] leading-relaxed bg-linen border-0 p-4 text-navy resize-none"
              data-testid="linkedin-post-text"
            />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(linkedInPost.post_text);
                    toast.success("Post copied to clipboard");
                  } catch (e) {
                    toast.error("Copy failed — please copy manually");
                  }
                }}
                className="btn-brass inline-flex items-center gap-2"
                data-testid="linkedin-copy"
              >
                <span className="text-lg leading-none">&#9733;</span>
                Copy Post
              </button>
              <span className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">
                {linkedInPost.character_count} characters
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Envelope compose modal */}
      {envelopeOpen && (
        <div
          className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="envelope-modal"
          onClick={() => setEnvelopeOpen(false)}
        >
          <form
            className="bg-ivory max-w-lg w-full p-8 md:p-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitEnvelope}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="kicker">Matching · No. 10</div>
                <h3 className="mt-2 font-serif text-2xl text-navy">Compose Envelope</h3>
                <p className="mt-2 text-sm text-slate2 font-light">
                  Leave the recipient blank to print a stack of return-address-only envelopes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEnvelopeOpen(false)}
                className="text-slate2 hover:text-navy text-xl leading-none"
                aria-label="Close"
                data-testid="envelope-modal-close"
              >
                ×
              </button>
            </div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">Recipient Name (optional)</label>
            <input
              type="text"
              value={envRecipient}
              onChange={(e) => setEnvRecipient(e.target.value)}
              className="field"
              data-testid="envelope-recipient"
              placeholder="Mr. John Q. Client"
            />
            <label className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">Address Line 1 (optional)</label>
            <input
              type="text"
              value={envLine1}
              onChange={(e) => setEnvLine1(e.target.value)}
              className="field"
              data-testid="envelope-line1"
              placeholder="123 Preston Trail"
            />
            <label className="text-[10px] uppercase tracking-[0.28em] text-slate2/70">City · State · ZIP (optional)</label>
            <input
              type="text"
              value={envLine2}
              onChange={(e) => setEnvLine2(e.target.value)}
              className="field"
              data-testid="envelope-line2"
              placeholder="Dallas, Texas 75225"
            />
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={envLoading}
                className="btn-brass inline-flex items-center gap-2"
                data-testid="envelope-submit"
              >
                {envLoading ? (
                  <>
                    <StarSpinner size="sm" label="" />
                    <span>Rendering…</span>
                  </>
                ) : (
                  <>
                    <span className="text-lg leading-none">&#9733;</span>
                    <span>Generate PDF</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setEnvelopeOpen(false)}
                className="btn-primary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Future closet placeholder */}
      <div
        className="mt-14 p-8 bg-linen/50 font-cormorant italic text-slate2 text-base leading-relaxed max-w-4xl"
        data-testid="stationery-closet"
      >
        <strong className="font-serif not-italic text-navy">The closet.</strong>{" "}
        Future additions will file here — the Matter Closing Letter, gift-card certificate,
        condolence card, and referral notes. Each new stationery piece will appear as its
        own card the moment it&apos;s drafted.
      </div>
    </div>
  );
}

function Card({ kicker, title, body, testId, children }) {
  return (
    <div className="bg-ivory p-8 md:p-10" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <span className="text-brass text-lg leading-none mt-1">&#9733;</span>
        <div>
          <div className="kicker">{kicker}</div>
          <h3 className="mt-2 font-serif text-2xl text-navy leading-tight">{title}</h3>
        </div>
      </div>
      <p className="text-slate2 font-cormorant italic text-base leading-relaxed mb-6">
        {body}
      </p>
      {children}
    </div>
  );
}
